import { Trans, t } from '@lingui/macro';
import {
    FieldSelect,
    Flexbox,
    Link,
    PrimaryButton,
    SecondaryButton,
    Tag,
    Text,
    colorSystem,
} from '@luminovo/design-system';
import { OtsFullPart, PartAlternative, TechnicalProperties } from '@luminovo/http-client';
import { ComplianceStatusChip, LifecycleChip, SimilarityTag } from '@luminovo/sourcing-core';
import { DescriptionOutlined } from '@mui/icons-material';
import { Tooltip } from '@mui/material';
import { useSnackbar } from 'notistack';
import {
    formatCapacitance,
    formatDielectricMaterial,
    formatPowerRating,
    formatResistance,
    formatTemperatureCoefficient,
    formatTolerance,
    formatVoltageRating,
} from '../../../utils/converterUtils';
import { route } from '../../../utils/routes';
import { StockView } from '../../StockView';
import { PartImage } from '../commons/PartImage';
import { PartAttribute, PartComparisonContext } from './types';

enum AttributesIndex {
    PartImage = 'partImage',
    MpnManufacturer = 'mpnManufacturer',
    Similarity = 'similarity',
    Description = 'description',
    Datasheet = 'datasheet',
    PartType = 'partType',
    SourcingHeader = 'sourcingHeader',
    Availability = 'availability',
    SpecificationsHeader = 'specificationsHeader',
    Resistance = 'resistance',
    Capacitance = 'capacitance',
    Tolerance = 'tolerance',
    PowerRating = 'powerRating',
    Dielectric = 'dielectric',
    VoltageRating = 'voltageRating',
    TempCoefficient = 'tempCoefficient',
    PackageHeader = 'packageHeader',
    PackageName = 'packageName',
    Mounting = 'mounting',
    NumberOfPins = 'numberOfPins',
    ComplianceHeader = 'complianceHeader',
    RohsCompliant = 'rohsCompliant',
    ReachCompliant = 'reachCompliant',
    AecqCompliant = 'aecqCompliant',
    LifecycleHeader = 'lifecycleHeader',
    Lifecycle = 'lifecycle',
    LifecycleYteol = 'lifecycleYteol',
}

const RenderAlternativeImageAttribute = ({
    partAlternative,
    context,
    isColumnHovered,
}: {
    partAlternative: PartAlternative;
    context: PartComparisonContext;
    isColumnHovered: boolean;
}) => {
    const { enqueueSnackbar } = useSnackbar();
    const { handleLinkPart, partAlternativesTableState, canUserEditComponent } = context;
    const part = partAlternative.off_the_shelf_part;
    const { dispatch } = partAlternativesTableState;

    return (
        <Flexbox justifyContent="center" style={{ position: 'relative' }}>
            <PartImage imageUrl={partAlternative.off_the_shelf_part.image_url} isHovered={isColumnHovered} />
            <PrimaryButton
                size="small"
                disabled={!canUserEditComponent}
                onClick={() => {
                    handleLinkPart(part);
                    dispatch({ type: 'select-items', ids: [part.id], selected: false });
                    enqueueSnackbar(part.mpn + ', ' + part.manufacturer.name + ' ' + t`approved`, {
                        variant: 'success',
                    });
                }}
                style={{
                    margin: 'auto',
                    width: 'auto',
                    display: isColumnHovered ? 'block' : 'none',
                    position: 'absolute',
                    top: '45px',
                }}
            >
                <Trans>Approve part</Trans>
            </PrimaryButton>
            <SecondaryButton
                size="small"
                onClick={() =>
                    window.open(route('/parts/details/:partId', { partId: part.id }), '_blank', 'noopener noreferrer')
                }
                style={{
                    margin: 'auto',
                    width: 'auto',
                    display: isColumnHovered ? 'block' : 'none',
                    position: 'absolute',
                    top: '75px',
                }}
            >
                <Trans>Open part page</Trans>
            </SecondaryButton>
        </Flexbox>
    );
};

const attributeImage: PartAttribute = {
    id: AttributesIndex.PartImage,
    label: '',
    isVisible: () => true,
    renderItem: ({ data: part }) => {
        return (
            <Flexbox justifyContent="center">
                <PartImage imageUrl={part.image_url} showBackground={false} />
            </Flexbox>
        );
    },
    renderComparableItem: (part, context) => {
        return <RenderAlternativeImageAttribute context={context} isColumnHovered={false} partAlternative={part} />;
    },
    renderComparableItemOnHover: (part, context) => {
        return <RenderAlternativeImageAttribute context={context} isColumnHovered={true} partAlternative={part} />;
    },
};

const renderOption = (part: OtsFullPart) => {
    return part.mpn + ', ' + part.manufacturer.name;
};

const renderLabel = (part: OtsFullPart) => {
    return part.mpn + ', ' + part.manufacturer.name;
};

const attributeMpnManufacturer: PartAttribute = {
    id: AttributesIndex.MpnManufacturer,
    label: <Trans>MPN, manufacturer</Trans>,
    isVisible: () => true,
    renderItem: ({ data: selectedPart, dispatch }, sharedContext) => {
        const { linkedOtsParts } = sharedContext;
        if (linkedOtsParts.length === 1) {
            return <Text variant="h4">{renderLabel(selectedPart)}</Text>;
        }
        return (
            <FieldSelect
                value={selectedPart}
                options={linkedOtsParts}
                onChange={(value) => dispatch({ type: 'setSelectedItem', selectedItem: value ?? selectedPart })}
                renderOption={renderOption}
                getOptionLabel={renderLabel}
                size="small"
                disableClearable
                style={{ background: 'white', color: colorSystem.neutral[7] }}
            />
        );
    },
    renderComparableItem: (data) => {
        return <Text variant="h4">{renderLabel(data.off_the_shelf_part)}</Text>;
    },
};

const attributeSimilarity: PartAttribute = {
    id: AttributesIndex.Similarity,
    label: <Trans>Similarity</Trans>,
    isVisible: () => true,
    renderItem: () => {
        return <Tag color="neutral" attention="low" label={t`Original`} />;
    },
    renderComparableItem: (data) => {
        return <SimilarityTag similarity={data.similarity} />;
    },
};

function descriptionTooltip(part: OtsFullPart | null | undefined) {
    if (!part || !part.description) {
        return '';
    }
    // Only show the tooltip if the description is truncated
    return part.description.length > 120 ? part.description : '';
}

const attributeDescription: PartAttribute = {
    id: AttributesIndex.Description,
    label: <Trans>Description</Trans>,
    isVisible: () => true,
    renderItem: ({ data: part }) => {
        return (
            <Tooltip title={descriptionTooltip(part)} placement="top">
                <span>{part.description ?? '-'}</span>
            </Tooltip>
        );
    },
    renderComparableItem: (data) => {
        const part = data.off_the_shelf_part;
        return (
            <Tooltip title={descriptionTooltip(part)} placement="top">
                <span>{part.description ?? '-'}</span>
            </Tooltip>
        );
    },
};

const DatasheetLink = ({ url }: { url: string | null | undefined }) => {
    return (
        <Link
            href={url ?? undefined}
            attention="high"
            target="_blank"
            rel="noopener noreferrer"
            startIcon={<DescriptionOutlined />}
        >
            <Trans>Datasheet</Trans>
        </Link>
    );
};

const attributeDatasheet: PartAttribute = {
    id: AttributesIndex.Datasheet,
    label: <Trans>Datasheet</Trans>,
    isVisible: () => true,
    renderItem: ({ data: part }) => {
        return <DatasheetLink url={part.datasheet_url} />;
    },
    renderComparableItem: (data) => {
        const part = data.off_the_shelf_part;
        return <DatasheetLink url={part.datasheet_url} />;
    },
};

function isResistor({ partType }: { partType: string | null | undefined }): boolean {
    return partType === 'Resistor' || partType === 'Resistors';
}

function isCapacitor({ partType }: { partType: string | null | undefined }): boolean {
    return partType === 'Capacitor' || partType === 'Capacitors';
}

const attributePartType: PartAttribute = {
    id: AttributesIndex.PartType,
    label: <Trans>Type</Trans>,
    isVisible: ({ data: part, comparableData: partAlternatives, dialogState }) => {
        if (dialogState.viewMode === 'show-all') {
            return true;
        }
        return !partAlternatives.every((alternative) => {
            const alternativeType = alternative.off_the_shelf_part.type;
            const otsType = part.type;
            return (
                alternativeType === otsType ||
                (isCapacitor({ partType: alternativeType }) && isCapacitor({ partType: otsType })) ||
                (isResistor({ partType: alternativeType }) && isResistor({ partType: otsType }))
            );
        });
    },
    renderItem: ({ data: part }) => {
        return <>{part.type ?? '-'}</>;
    },
    renderComparableItem: (data) => {
        const part = data.off_the_shelf_part;
        return <>{part.type ?? '-'}</>;
    },
};

function areResistorSpecsSimilar({
    partAlternatives,
    selectedPartSpecs,
}: {
    partAlternatives: PartAlternative[];
    selectedPartSpecs: TechnicalProperties | null;
}): boolean {
    const resistance = selectedPartSpecs?.resistance ? formatResistance(selectedPartSpecs?.resistance) : '-';
    const tolerance = selectedPartSpecs?.tolerance ? formatTolerance(selectedPartSpecs?.tolerance) : '-';
    const powerRating = selectedPartSpecs?.power_rating ? formatPowerRating(selectedPartSpecs?.power_rating) : '-';
    const voltageRating = selectedPartSpecs?.voltage_rating
        ? formatVoltageRating(selectedPartSpecs?.voltage_rating)
        : '-';
    const temperatureCoefficient = selectedPartSpecs?.temperature_coefficient
        ? formatTemperatureCoefficient(selectedPartSpecs?.temperature_coefficient)
        : '-';

    return !partAlternatives.every((alternative) => {
        const alternativeResistance = alternative.off_the_shelf_part.technical_properties?.resistance
            ? formatResistance(alternative.off_the_shelf_part.technical_properties?.resistance)
            : '-';
        const alternativeTolerance = alternative.off_the_shelf_part.technical_properties?.tolerance
            ? formatTolerance(alternative.off_the_shelf_part.technical_properties?.tolerance)
            : '-';
        const alternativePowerRating = alternative.off_the_shelf_part.technical_properties?.power_rating
            ? formatPowerRating(alternative.off_the_shelf_part.technical_properties?.power_rating)
            : '-';
        const alternativeVoltageRating = alternative.off_the_shelf_part.technical_properties?.voltage_rating
            ? formatVoltageRating(alternative.off_the_shelf_part.technical_properties?.voltage_rating)
            : '-';
        const alternativeTemperatureCoefficient = alternative.off_the_shelf_part.technical_properties
            ?.temperature_coefficient
            ? formatTemperatureCoefficient(alternative.off_the_shelf_part.technical_properties?.temperature_coefficient)
            : '-';
        return (
            resistance === alternativeResistance &&
            tolerance === alternativeTolerance &&
            powerRating === alternativePowerRating &&
            voltageRating === alternativeVoltageRating &&
            temperatureCoefficient === alternativeTemperatureCoefficient
        );
    });
}

function areCapacitorSpecsSimilar({
    partAlternatives,
    selectedPartSpecs,
}: {
    partAlternatives: PartAlternative[];
    selectedPartSpecs: TechnicalProperties | null;
}): boolean {
    const capacitance = selectedPartSpecs?.capacitance ? formatCapacitance(selectedPartSpecs?.capacitance) : '-';
    const tolerance = selectedPartSpecs?.tolerance ? formatTolerance(selectedPartSpecs?.tolerance) : '-';
    const dielectric = selectedPartSpecs?.dielectric ? formatDielectricMaterial(selectedPartSpecs?.dielectric) : '-';
    const voltageRating = selectedPartSpecs?.voltage_rating
        ? formatVoltageRating(selectedPartSpecs?.voltage_rating)
        : '-';

    return !partAlternatives.every((alternative) => {
        const alternativeCapacitance = alternative.off_the_shelf_part.technical_properties?.capacitance
            ? formatCapacitance(alternative.off_the_shelf_part.technical_properties?.capacitance)
            : '-';
        const alternativeTolerance = alternative.off_the_shelf_part.technical_properties?.tolerance
            ? formatTolerance(alternative.off_the_shelf_part.technical_properties?.tolerance)
            : '-';
        const alternativeDielectric = alternative.off_the_shelf_part.technical_properties?.dielectric
            ? formatDielectricMaterial(alternative.off_the_shelf_part.technical_properties?.dielectric)
            : '-';
        const alternativeVoltageRating = alternative.off_the_shelf_part.technical_properties?.voltage_rating
            ? formatVoltageRating(alternative.off_the_shelf_part.technical_properties?.voltage_rating)
            : '-';

        return (
            capacitance === alternativeCapacitance &&
            tolerance === alternativeTolerance &&
            dielectric === alternativeDielectric &&
            voltageRating === alternativeVoltageRating
        );
    });
}

const specificationsHeader: PartAttribute = {
    id: AttributesIndex.SpecificationsHeader,
    label: (
        <Text variant="h4">
            <Trans>Specifications</Trans>
        </Text>
    ),
    isVisible: ({ data: part, comparableData: partAlternatives, dialogState }) => {
        if (
            dialogState.viewMode === 'show-all' &&
            (isResistor({ partType: part.type }) || isCapacitor({ partType: part.type }))
        ) {
            return true;
        }
        if (isResistor({ partType: part.type })) {
            return areResistorSpecsSimilar({ partAlternatives, selectedPartSpecs: part.technical_properties });
        }
        if (isCapacitor({ partType: part.type })) {
            return areCapacitorSpecsSimilar({ partAlternatives, selectedPartSpecs: part.technical_properties });
        }
        return false;
    },
    renderItem: () => {
        return <></>;
    },
    renderComparableItem: () => {
        return <></>;
    },
};

const attributeResistance: PartAttribute = {
    id: AttributesIndex.Resistance,
    label: <Trans>Resistance</Trans>,
    isVisible: ({ data: part, comparableData: partAlternatives, dialogState }) => {
        if (dialogState.viewMode === 'show-all' && isResistor({ partType: part.type })) {
            return true;
        }
        const selectedPartResistance = part.technical_properties?.resistance
            ? formatResistance(part.technical_properties?.resistance)
            : '-';

        return (
            isResistor({ partType: part.type }) &&
            !partAlternatives.every((alternative) => {
                const alternativeResistance = alternative.off_the_shelf_part.technical_properties?.resistance
                    ? formatResistance(alternative.off_the_shelf_part.technical_properties?.resistance)
                    : '-';
                return selectedPartResistance === alternativeResistance;
            })
        );
    },
    renderItem: ({ data: part }) => {
        const resistance = part.technical_properties?.resistance;
        return <>{resistance ? formatResistance(resistance) : '-'}</>;
    },
    renderComparableItem: (data) => {
        const resistance = data.off_the_shelf_part.technical_properties?.resistance;
        return <>{resistance ? formatResistance(resistance) : '-'}</>;
    },
};

const attributeCapacitance: PartAttribute = {
    id: AttributesIndex.Capacitance,
    label: <Trans>Capacitance</Trans>,
    isVisible: ({ data: part, comparableData: partAlternatives, dialogState }) => {
        if (dialogState.viewMode === 'show-all' && isCapacitor({ partType: part.type })) {
            return true;
        }
        const selectedPartCapacitance = part.technical_properties?.capacitance
            ? formatCapacitance(part.technical_properties?.capacitance)
            : '-';

        return (
            isCapacitor({ partType: part.type }) &&
            !partAlternatives.every((alternative) => {
                const alternativeCapacitance = alternative.off_the_shelf_part.technical_properties?.capacitance
                    ? formatCapacitance(alternative.off_the_shelf_part.technical_properties?.capacitance)
                    : '-';
                return selectedPartCapacitance === alternativeCapacitance;
            })
        );
    },
    renderItem: ({ data: part }) => {
        const capacitance = part.technical_properties?.capacitance;
        return <>{capacitance ? formatCapacitance(capacitance) : '-'}</>;
    },
    renderComparableItem: (data) => {
        const capacitance = data.off_the_shelf_part.technical_properties?.capacitance;
        return <>{capacitance ? formatCapacitance(capacitance) : '-'}</>;
    },
};

const attributeTolerance: PartAttribute = {
    id: AttributesIndex.Tolerance,
    label: <Trans>Tolerance</Trans>,
    isVisible: ({ data: part, comparableData: partAlternatives, dialogState }) => {
        if (
            dialogState.viewMode === 'show-all' &&
            (isResistor({ partType: part.type }) || isCapacitor({ partType: part.type }))
        ) {
            return true;
        }
        const selectedPartTolerance = part.technical_properties?.tolerance
            ? formatTolerance(part.technical_properties?.tolerance)
            : '-';

        return (
            (isResistor({ partType: part.type }) || isCapacitor({ partType: part.type })) &&
            !partAlternatives.every((alternative) => {
                const alternativeTolerance = alternative.off_the_shelf_part.technical_properties?.tolerance
                    ? formatTolerance(alternative.off_the_shelf_part.technical_properties?.tolerance)
                    : '-';
                return selectedPartTolerance === alternativeTolerance;
            })
        );
    },
    renderItem: ({ data: part }) => {
        const tolerance = part.technical_properties?.tolerance;
        return <>{tolerance ? formatTolerance(tolerance) : '-'}</>;
    },
    renderComparableItem: (data) => {
        const tolerance = data.off_the_shelf_part.technical_properties?.tolerance;
        return <>{tolerance ? formatTolerance(tolerance) : '-'}</>;
    },
};

const attributePowerRating: PartAttribute = {
    id: AttributesIndex.PowerRating,
    label: <Trans>Power Rating</Trans>,
    isVisible: ({ data: part, comparableData: partAlternatives, dialogState }) => {
        if (dialogState.viewMode === 'show-all' && isResistor({ partType: part.type })) {
            return true;
        }
        const selectedPartPowerRating = part.technical_properties?.power_rating
            ? formatPowerRating(part.technical_properties?.power_rating)
            : '-';

        return (
            isResistor({ partType: part.type }) &&
            !partAlternatives.every((alternative) => {
                const alternativePowerRating = alternative.off_the_shelf_part.technical_properties?.power_rating
                    ? formatPowerRating(alternative.off_the_shelf_part.technical_properties?.power_rating)
                    : '-';
                return selectedPartPowerRating === alternativePowerRating;
            })
        );
    },
    renderItem: ({ data: part }) => {
        const powerRating = part.technical_properties?.power_rating;
        return <>{powerRating ? formatPowerRating(powerRating) : '-'}</>;
    },
    renderComparableItem: (data) => {
        const powerRating = data.off_the_shelf_part.technical_properties?.power_rating;
        return <>{powerRating ? formatPowerRating(powerRating) : '-'}</>;
    },
};

const attributeDielectric: PartAttribute = {
    id: AttributesIndex.Dielectric,
    label: <Trans>Dielectric</Trans>,
    isVisible: ({ data: part, comparableData: partAlternatives, dialogState }) => {
        if (dialogState.viewMode === 'show-all' && isCapacitor({ partType: part.type })) {
            return true;
        }
        const selectedPartDielectric = part.technical_properties?.dielectric
            ? formatDielectricMaterial(part.technical_properties?.dielectric)
            : '-';

        return (
            isCapacitor({ partType: part.type }) &&
            !partAlternatives.every((alternative) => {
                const alternativeDielectric = alternative.off_the_shelf_part.technical_properties?.dielectric
                    ? formatDielectricMaterial(alternative.off_the_shelf_part.technical_properties?.dielectric)
                    : '-';
                return selectedPartDielectric === alternativeDielectric;
            })
        );
    },
    renderItem: ({ data: part }) => {
        const dielectric = part.technical_properties?.dielectric;
        return <>{dielectric ? formatDielectricMaterial(dielectric) : '-'}</>;
    },
    renderComparableItem: (data) => {
        const dielectric = data.off_the_shelf_part.technical_properties?.dielectric;
        return <>{dielectric ? formatDielectricMaterial(dielectric) : '-'}</>;
    },
};

const attributeVoltageRating: PartAttribute = {
    id: AttributesIndex.VoltageRating,
    label: <Trans>Voltage rating</Trans>,
    isVisible: ({ data: part, comparableData: partAlternatives, dialogState }) => {
        if (
            dialogState.viewMode === 'show-all' &&
            (isResistor({ partType: part.type }) || isCapacitor({ partType: part.type }))
        ) {
            return true;
        }
        const selectedPartVoltageRating = part.technical_properties?.voltage_rating
            ? formatVoltageRating(part.technical_properties?.voltage_rating)
            : '-';

        return (
            (isResistor({ partType: part.type }) || isCapacitor({ partType: part.type })) &&
            !partAlternatives.every((alternative) => {
                const alternativeVoltageRating = alternative.off_the_shelf_part.technical_properties?.voltage_rating
                    ? formatVoltageRating(alternative.off_the_shelf_part.technical_properties?.voltage_rating)
                    : '-';
                return selectedPartVoltageRating === alternativeVoltageRating;
            })
        );
    },
    renderItem: ({ data: part }) => {
        const voltageRating = part.technical_properties?.voltage_rating;
        return <>{voltageRating ? formatVoltageRating(voltageRating) : '-'}</>;
    },
    renderComparableItem: (data) => {
        const voltageRating = data.off_the_shelf_part.technical_properties?.voltage_rating;
        return <>{voltageRating ? formatVoltageRating(voltageRating) : '-'}</>;
    },
};

const attributeTempCoefficient: PartAttribute = {
    id: AttributesIndex.TempCoefficient,
    label: <Trans>Temperature coefficient</Trans>,
    isVisible: ({ data: part, comparableData: partAlternatives, dialogState }) => {
        if (dialogState.viewMode === 'show-all' && isResistor({ partType: part.type })) {
            return true;
        }
        const selectedPartTempCoefficient = part.technical_properties?.temperature_coefficient
            ? formatTemperatureCoefficient(part.technical_properties?.temperature_coefficient)
            : '-';

        return (
            isResistor({ partType: part.type }) &&
            !partAlternatives.every((alternative) => {
                const alternativeTempCoefficient = alternative.off_the_shelf_part.technical_properties
                    ?.temperature_coefficient
                    ? formatTemperatureCoefficient(
                          alternative.off_the_shelf_part.technical_properties?.temperature_coefficient,
                      )
                    : '-';
                return selectedPartTempCoefficient === alternativeTempCoefficient;
            })
        );
    },
    renderItem: ({ data: part }) => {
        const temperatureCoefficient = part.technical_properties?.temperature_coefficient;
        return <>{temperatureCoefficient ? formatTemperatureCoefficient(temperatureCoefficient) : '-'}</>;
    },
    renderComparableItem: (data) => {
        const temperatureCoefficient = data.off_the_shelf_part.technical_properties?.temperature_coefficient;
        return <>{temperatureCoefficient ? formatTemperatureCoefficient(temperatureCoefficient) : '-'}</>;
    },
};

const packageHeader: PartAttribute = {
    id: AttributesIndex.PackageHeader,
    label: (
        <Text variant="h4">
            <Trans>Package</Trans>
        </Text>
    ),
    isVisible: ({ data: part, comparableData: partAlternatives, dialogState }) => {
        if (dialogState.viewMode === 'show-all') {
            return true;
        }
        return !partAlternatives.every(
            (alternative) =>
                alternative.off_the_shelf_part.package?.name === part.package?.name &&
                alternative.off_the_shelf_part.package?.mounting === part.package?.mounting &&
                alternative.off_the_shelf_part.package?.number_of_pins === part.package?.number_of_pins,
        );
    },
    renderItem: () => {
        return <></>;
    },
    renderComparableItem: () => {
        return <></>;
    },
};

const attributePackageName: PartAttribute = {
    id: AttributesIndex.PackageName,
    label: <Trans>Name</Trans>,
    isVisible: ({ data: part, comparableData: partAlternatives, dialogState }) => {
        if (dialogState.viewMode === 'show-all') {
            return true;
        }
        return !partAlternatives.every(
            (alternative) => alternative.off_the_shelf_part.package?.name === part.package?.name,
        );
    },
    renderItem: ({ data: part }) => {
        return <>{part.package?.name ?? '-'}</>;
    },
    renderComparableItem: (data) => {
        const part = data.off_the_shelf_part;
        return <>{part.package?.name ?? '-'}</>;
    },
};

const attributeMounting: PartAttribute = {
    id: AttributesIndex.Mounting,
    label: <Trans>Mounting</Trans>,
    isVisible: ({ data: part, comparableData: partAlternatives, dialogState }) => {
        if (dialogState.viewMode === 'show-all') {
            return true;
        }
        return !partAlternatives.every(
            (alternative) => alternative.off_the_shelf_part.package?.mounting === part.package?.mounting,
        );
    },
    renderItem: ({ data: part }) => {
        return <>{part.package?.mounting ?? '-'}</>;
    },
    renderComparableItem: (data) => {
        const part = data.off_the_shelf_part;
        return <>{part.package?.mounting ?? '-'}</>;
    },
};

const attributeNumberOfPins: PartAttribute = {
    id: AttributesIndex.NumberOfPins,
    label: <Trans>Number of pins</Trans>,
    isVisible: ({ data: part, comparableData: partAlternatives, dialogState }) => {
        if (dialogState.viewMode === 'show-all') {
            return true;
        }
        return !partAlternatives.every(
            (alternative) => alternative.off_the_shelf_part.package?.number_of_pins === part.package?.number_of_pins,
        );
    },
    renderItem: ({ data: part }) => {
        return <>{part.package?.number_of_pins ?? '-'}</>;
    },
    renderComparableItem: (data) => {
        const part = data.off_the_shelf_part;
        return <>{part.package?.number_of_pins ?? '-'}</>;
    },
};

const complianceHeader: PartAttribute = {
    id: AttributesIndex.ComplianceHeader,
    label: (
        <Text variant="h4">
            <Trans>Compliance</Trans>
        </Text>
    ),
    isVisible: ({ data: part, comparableData: partAlternatives, dialogState }) => {
        if (dialogState.viewMode === 'show-all') {
            return true;
        }

        return !partAlternatives.every(
            (alternative) =>
                alternative.off_the_shelf_part.rohs_compliant === part.rohs_compliant &&
                alternative.off_the_shelf_part.reach_compliant === part.reach_compliant &&
                alternative.off_the_shelf_part.aecq_compliant === part.aecq_compliant,
        );
    },
    renderItem: () => {
        return <></>;
    },
    renderComparableItem: () => {
        return <></>;
    },
};

const attributeRohsCompliant: PartAttribute = {
    id: AttributesIndex.RohsCompliant,
    label: <Trans>RoHS</Trans>,
    isVisible: ({ data: part, comparableData: partAlternatives, dialogState }) => {
        if (dialogState.viewMode === 'show-all') {
            return true;
        }
        return !partAlternatives.every(
            (alternative) => alternative.off_the_shelf_part.rohs_compliant === part.rohs_compliant,
        );
    },
    renderItem: ({ data: part }) => {
        return <ComplianceStatusChip status={part.rohs_compliant} />;
    },
    renderComparableItem: (data) => {
        const part = data.off_the_shelf_part;
        return <ComplianceStatusChip status={part.rohs_compliant} />;
    },
};

const attributeReachCompliant: PartAttribute = {
    id: AttributesIndex.ReachCompliant,
    label: <Trans>REACH</Trans>,
    isVisible: ({ data: part, comparableData: partAlternatives, dialogState }) => {
        if (dialogState.viewMode === 'show-all') {
            return true;
        }
        return !partAlternatives.every(
            (alternative) => alternative.off_the_shelf_part.reach_compliant === part.reach_compliant,
        );
    },
    renderItem: ({ data: part }) => {
        return <ComplianceStatusChip status={part.reach_compliant} />;
    },
    renderComparableItem: (data) => {
        const part = data.off_the_shelf_part;
        return <ComplianceStatusChip status={part.reach_compliant} />;
    },
};

const attributeAecqCompliant: PartAttribute = {
    id: AttributesIndex.AecqCompliant,
    label: <Trans>AECQ</Trans>,
    isVisible: ({ data: part, comparableData: partAlternatives, dialogState }) => {
        if (dialogState.viewMode === 'show-all') {
            return true;
        }
        return !partAlternatives.every(
            (alternative) => alternative.off_the_shelf_part.aecq_compliant === part.aecq_compliant,
        );
    },
    renderItem: ({ data: part }) => {
        return <ComplianceStatusChip status={part.aecq_compliant} />;
    },
    renderComparableItem: (data) => {
        const part = data.off_the_shelf_part;
        return <ComplianceStatusChip status={part.aecq_compliant} />;
    },
};

const lifecycleHeader: PartAttribute = {
    id: AttributesIndex.LifecycleHeader,
    label: (
        <Text variant="h4">
            <Trans>Lifecycle</Trans>
        </Text>
    ),
    isVisible: ({ data: part, comparableData: partAlternatives, dialogState }) => {
        if (dialogState.viewMode === 'show-all') {
            return true;
        }
        return !partAlternatives.every(
            (alternative) =>
                alternative.off_the_shelf_part.lifecycle_status === part.lifecycle_status &&
                alternative.off_the_shelf_part.lifecycle_yteol === part.lifecycle_yteol,
        );
    },
    renderItem: () => {
        return <></>;
    },
    renderComparableItem: () => {
        return <></>;
    },
};

const attributelifecycle: PartAttribute = {
    id: AttributesIndex.Lifecycle,
    label: <Trans>Status</Trans>,
    isVisible: ({ data: part, comparableData: partAlternatives, dialogState }) => {
        if (dialogState.viewMode === 'show-all') {
            return true;
        }
        return !partAlternatives.every(
            (alternative) => alternative.off_the_shelf_part.lifecycle_status === part.lifecycle_status,
        );
    },
    renderItem: ({ data: part }) => {
        return <LifecycleChip lifecycle={part.lifecycle_status} />;
    },
    renderComparableItem: (data) => {
        const part = data.off_the_shelf_part;
        return <LifecycleChip lifecycle={part.lifecycle_status} />;
    },
};

const attributeLifecycleYteol: PartAttribute = {
    id: AttributesIndex.LifecycleYteol,
    label: <Trans>Years-to-EOL</Trans>,
    isVisible: ({ data: part, comparableData: partAlternatives, dialogState }) => {
        if (dialogState.viewMode === 'show-all') {
            return true;
        }
        return !partAlternatives.every(
            (alternative) => alternative.off_the_shelf_part.lifecycle_yteol === part.lifecycle_yteol,
        );
    },
    renderItem: ({ data: part }) => {
        return <>{part.lifecycle_yteol ?? '-'}</>;
    },
    renderComparableItem: (data) => {
        const part = data.off_the_shelf_part;
        return <>{part.lifecycle_yteol ?? '-'}</>;
    },
};

const sourcingHeader: PartAttribute = {
    id: AttributesIndex.SourcingHeader,
    label: (
        <Text variant="h4">
            <Trans>Sourcing</Trans>
        </Text>
    ),
    isVisible: () => true,
    renderItem: () => {
        return <></>;
    },
    renderComparableItem: () => {
        return <></>;
    },
};

const attributeAvailability: PartAttribute = {
    id: AttributesIndex.Availability,
    label: <Trans>Availability</Trans>,
    isVisible: () => true,
    renderItem: ({ data: part }) => {
        return <StockView isLoading={!Boolean(part.sourcingSummary)} quantity={part.sourcingSummary?.supplier_stock} />;
    },
    renderComparableItem: (data) => {
        return <StockView isLoading={!Boolean(data.sourcingSummary)} quantity={data.sourcingSummary?.supplier_stock} />;
    },
};

export const Attributes = [
    attributeImage,
    attributeMpnManufacturer,
    attributeSimilarity,
    attributeDescription,
    attributeDatasheet,
    attributePartType,
    sourcingHeader,
    attributeAvailability,
    specificationsHeader,
    attributeResistance,
    attributeCapacitance,
    attributeTolerance,
    attributePowerRating,
    attributeDielectric,
    attributeVoltageRating,
    attributeTempCoefficient,
    packageHeader,
    attributePackageName,
    attributeMounting,
    attributeNumberOfPins,
    complianceHeader,
    attributeRohsCompliant,
    attributeReachCompliant,
    attributeAecqCompliant,
    lifecycleHeader,
    attributelifecycle,
    attributeLifecycleYteol,
];
